import { useState, createContext, useContext } from "react";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import featuresReducer, {
  initialState as featuresInitialState,
} from "./featuresReducer";
import { useImmerReducer } from "use-immer";
import SketchViewModel from "esri/widgets/Sketch/SketchViewModel";
import { IProps as DialogProps } from "../components/organisms/Dialog";
import { initialDialogProps } from "../containers/Other/DialogContainer";
import { initialDialogProps as initialMapDialogProps } from "../containers/Other/DialogMapContainer";
import Map from "@arcgis/core/Map";
import appRequirementsReducer, {
  initialState as appRequirementsInitialState,
} from "./appRequirementsReducer";
import MapView from "@arcgis/core/views/MapView";
import { mapModes, nvrToolModes } from "../lib/types";

const NvrToolModeContext = createContext<any>(null);
const MapContext = createContext<any>(null);
const FeaturesContext = createContext<any>(null);
const FeaturesDispatchContext = createContext<any>(null);
const AppRequirementsContext = createContext<any>(null);
const AppRequirementsDispatchContext = createContext<any>(null);
const DrawingContext = createContext<any>(null);
const MapModeContext = createContext<any>(null);
const DialogContext = createContext<any>(null);
const MapDialogContext = createContext<any>(null);
const MapMessagingContext = createContext<any>(null);
const MapScaleContext = createContext<any>(null);
const MiscellaneousContext = createContext<any>(null);

export function useNvrToolModeContext() {
  return useContext(NvrToolModeContext);
}

export function useMapContext() {
  return useContext(MapContext);
}

export function useFeaturesContext() {
  return useContext(FeaturesContext);
}

export function useFeaturesDispatchContext() {
  return useContext(FeaturesDispatchContext);
}

export function useAppRequirementsContext() {
  return useContext(AppRequirementsContext);
}

export function useAppRequirementsDispatchContext() {
  return useContext(AppRequirementsDispatchContext);
}

export function useDrawingContext() {
  return useContext(DrawingContext);
}

export function useMapModeContext() {
  return useContext(MapModeContext);
}

export function useDialogContext() {
  return useContext(DialogContext);
}

export function useMapDialogContext() {
  return useContext(MapDialogContext);
}

export function useMapMessagingContext() {
  return useContext(MapMessagingContext);
}

export function useMapScaleContext() {
  return useContext(MapScaleContext);
}

export function useMiscellaneousContext() {
  return useContext(MiscellaneousContext);
}

// used for patches and reveg areas
const polygonGraphicsLayer = new GraphicsLayer({
  id: "polygons",
});

// used for trees and temp drawings (it will sit on top of the polygonGraphicsLayer)
const pointGraphicsLayer = new GraphicsLayer({
  id: "points",
});

// tree buffers only
const treeBufferGraphicsLayer = new GraphicsLayer({
  id: "treeBuffer",
});

// labels for all features
const labelGraphicsLayer = new GraphicsLayer({
  id: "label",
});

interface IProps {
  children: any;
}

function ContextProvider({ children }: IProps) {
  const [nvrToolMode, setNvrToolMode] = useState<nvrToolModes>();
  const [features, dispatch] = useImmerReducer(
    featuresReducer as any,
    featuresInitialState
  );
  const [appRequirements, appRequirementsDispatch] = useImmerReducer(
    appRequirementsReducer as any,
    appRequirementsInitialState
  );
  const [map, setMap] = useState<Map>();
  const [mapView, setMapView] = useState<MapView>();
  const [sketchVm, setSketchVm] = useState<SketchViewModel>();
  const [mapMode, setMapMode] = useState<mapModes>();
  const [dialogProps, setDialogProps] =
    useState<DialogProps>(initialDialogProps);
  const [mapDialogProps, setMapDialogProps] = useState<DialogProps>(
    initialMapDialogProps
  );
  const [mapMessage, setMapMessage] = useState<string | undefined>();
  const [mapMessageType, setMapMessageType] = useState<string | undefined>();
  const [mapScale, setMapScale] = useState(0);
  const [uploading, setUploading] = useState(false);

  const nvrToolContextValue = {
    nvrToolMode,
    setNvrToolMode,
  };

  const mapContextValue = {
    map,
    mapView,
    setMap,
    setMapView,
  };

  const drawingContextValue = {
    sketchVm,
    setSketchVm,
    polygonGraphicsLayer,
    pointGraphicsLayer,
    treeBufferGraphicsLayer,
    labelGraphicsLayer,
  };

  const mapModeContextValue = {
    mapMode,
    setMapMode,
  };

  const dialogContextValue = {
    dialogProps,
    setDialogProps,
  };

  const mapDialogContextValue = {
    mapDialogProps,
    setMapDialogProps,
  };

  const mapMessagingContextValue = {
    mapMessage,
    mapMessageType,
    setMapMessage,
    setMapMessageType,
  };

  const mapScaleContextValue = {
    mapScale,
    setMapScale,
  };

  const miscellaneousContextValue = {
    uploading,
    setUploading,
  };

  return (
    <NvrToolModeContext.Provider value={nvrToolContextValue}>
      <MapContext.Provider value={mapContextValue}>
        <FeaturesContext.Provider value={features}>
          <FeaturesDispatchContext.Provider value={dispatch}>
            <AppRequirementsContext.Provider value={appRequirements}>
              <AppRequirementsDispatchContext.Provider
                value={appRequirementsDispatch}
              >
                <DrawingContext.Provider value={drawingContextValue}>
                  <MapModeContext.Provider value={mapModeContextValue}>
                    <DialogContext.Provider value={dialogContextValue}>
                      <MapDialogContext.Provider value={mapDialogContextValue}>
                        <MapMessagingContext.Provider
                          value={mapMessagingContextValue}
                        >
                          <MapScaleContext.Provider
                            value={mapScaleContextValue}
                          >
                            <MiscellaneousContext.Provider
                              value={miscellaneousContextValue}
                            >
                              {children}
                            </MiscellaneousContext.Provider>
                          </MapScaleContext.Provider>
                        </MapMessagingContext.Provider>
                      </MapDialogContext.Provider>
                    </DialogContext.Provider>
                  </MapModeContext.Provider>
                </DrawingContext.Provider>
              </AppRequirementsDispatchContext.Provider>
            </AppRequirementsContext.Provider>
          </FeaturesDispatchContext.Provider>
        </FeaturesContext.Provider>
      </MapContext.Provider>
    </NvrToolModeContext.Provider>
  );
}

export default ContextProvider;
